import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function SolitaireProCollectionPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Which solitaire games are included?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Solitaire Pro Collection includes classic Klondike, FreeCell, Spider, TriPeaks, Pyramid, and Golf. Each variant has its own rules and strategies, so you can mix short games with longer, strategic sessions.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Is every deal solvable?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Some solitaire variants are more forgiving than others. FreeCell is known for having solvable deals almost all the time, while Klondike and Spider depend more on draw order and smart sequencing.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What is the best first move in Klondike?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Prioritize uncovering face-down cards. Build alternating-color sequences and move cards to the foundations only when it does not block access to hidden cards.'
        }
      }
    ]
  };

  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Solitaire Pro Collection",
      "description": "Classic solitaire collection: Klondike, FreeCell, Spider, TriPeaks, Pyramid, and Golf.",
      "url": "https://logicpuzzles.ca/games/solitaire-pro-collection",
      "image": "https://logicpuzzles.ca/solitaireprocollection/icons/Icon-512.png",
      "genre": "Card Game",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/solitaireprocollection"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Solitaire Pro Collection - Classic Card Games Online | Logic Puzzles CA</title>
        <meta
          name="description"
          content="Play Solitaire Pro Collection online! Enjoy Klondike, FreeCell, Spider, TriPeaks, Pyramid, and Golf with smooth controls and strategic depth."
        />
        <meta
          name="keywords"
          content="solitaire online, klondike solitaire, freecell, spider solitaire, tripeaks, pyramid solitaire, golf solitaire, card games"
        />
        <meta property="og:title" content="Solitaire Pro Collection - Classic Card Games Online" />
        <meta
          property="og:description"
          content="A polished collection of classic solitaire games with smart hints, smooth play, and multiple difficulty options."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://logicpuzzles.ca/solitaireprocollection/icons/Icon-512.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Solitaire Pro Collection - Classic Card Games Online" />
        <meta
          name="twitter:description"
          content="Play Klondike, FreeCell, Spider, TriPeaks, Pyramid, and Golf in one polished solitaire collection."
        />
        <meta
          name="twitter:image"
          content="https://logicpuzzles.ca/solitaireprocollection/icons/Icon-512.png"
        />
        <link rel="canonical" href="https://logicpuzzles.ca/games/solitaire-pro-collection" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Card Games" categoryPath="/card-games" currentLabel="Solitaire Pro Collection" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Card Game · Free online</p>
            <h1>Solitaire Pro Collection</h1>
            <p className="gi-hero__lead">Six classic card games with modern polish and smooth play</p>
            <div className="gi-hero__actions">
              <Link to="/play/solitaireprocollection" className="button">▶ Play now</Link>
              <a href="#variants" className="button secondary">See the six games</a>
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/solitaireprocollection/icons/Icon-192.png" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="dlp" placement="game-page" page="/games/solitaire-pro-collection" headline="Like a daily brain workout? Get eight new logic puzzles every day in our free app" />

        <section className="gi-section">
          <p className="gi-lead">
            <strong>Solitaire Pro Collection</strong> brings together the most loved solitaire variants in
            one place. Whether you want a quick TriPeaks run or a deep Spider session, each game is tuned
            for clear rules, responsive controls, and satisfying strategic choices.
          </p>
        </section>

        <section className="gi-section" id="variants">
          <h2>Included Solitaire Variants</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <h3>Klondike</h3>
              <p>Build alternating-color sequences and reveal hidden cards to complete the foundations.</p>
            </div>
            <div className="gi-card">
              <h3>FreeCell</h3>
              <p>Use open cells as buffers to route cards with full information from the start.</p>
            </div>
            <div className="gi-card">
              <h3>Spider</h3>
              <p>Create descending sequences and clear full suits to the foundation piles.</p>
            </div>
            <div className="gi-card">
              <h3>TriPeaks</h3>
              <p>Clear the layout by playing cards one rank higher or lower than the foundation.</p>
            </div>
            <div className="gi-card">
              <h3>Pyramid</h3>
              <p>Remove pairs that add to 13 while keeping an eye on blocked cards.</p>
            </div>
            <div className="gi-card">
              <h3>Golf</h3>
              <p>Play through the deck by chaining cards that are one rank apart.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Strategy Tips That Actually Help</h2>
          <h3>Reveal Hidden Cards</h3>
          <ul className="gi-list">
            <li>Prioritize moves that turn over facedown cards.</li>
            <li>Use temporary moves that open new columns.</li>
            <li>Delay foundation moves if they lock your options.</li>
          </ul>
          <h3>Manage Space</h3>
          <ul className="gi-list">
            <li>Keep at least one open column or FreeCell when possible.</li>
            <li>Clear a tableau pile early to create flexibility.</li>
            <li>In Spider, focus on building same-suit runs.</li>
          </ul>
        </section>

        <section className="gi-section">
          <h2>Solitaire Pro Collection FAQ</h2>
          <div className="gi-faq">
            <details open>
              <summary>Which solitaire game is best for beginners?</summary>
              <p>Klondike and TriPeaks are great starting points with clear goals and fast feedback.</p>
            </details>
            <details>
              <summary>Does FreeCell always have a solution?</summary>
              <p>Most FreeCell deals are solvable, but your success depends on using the open cells wisely.</p>
            </details>
            <details>
              <summary>How do I win more often in Spider?</summary>
              <p>Build same-suit sequences when possible and avoid locking low cards under mixed suits.</p>
            </details>
          </div>
        </section>

        <section className="gi-cta">
          <h2>Ready to play?</h2>
          <p>
            Jump into a quick game or settle in for a longer session. Solitaire Pro Collection is free,
            mobile-friendly, and built for satisfying play.
          </p>
          <div className="gi-cta__actions">
            <Link to="/play/solitaireprocollection" className="button">Play Solitaire Pro Collection</Link>
            <Link to="/card-games" className="button secondary">More Card Games</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SolitaireProCollectionPage;
